import {
  Accessor,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  JSXElement,
  ParentComponent,
  Setter,
  untrack,
  useContext,
} from 'solid-js';
import { useQueryClient } from '@tanstack/solid-query';
import {
  EditableStop,
  TextureType,
  EditablePalette,
} from './types/heatmap.types';
import {
  HeatmapPaletteApiService,
  type PaletteWriteVars,
} from './heatmap-palette-api-service';
import { BACKEND_DEFAULT_PALETTE } from './heatmap-palette-defaults';
import { buildProjectTenantTrn } from '@trimble/utilities';

/** Whether the edit panel is adding a new palette or editing an existing one. */
export type EditMode = 'add' | 'edit';

export interface VolumeHeatmapContextValue {
  /** The full default palette, or null while loading. */
  readonly defaultPalette: Accessor<EditablePalette | null>;
  /** The colour stops for the default palette (empty while loading). */
  readonly defaultStops: Accessor<readonly EditableStop[]>;
  /** The texture type of the default palette ('gradient' | 'stepped'). */
  readonly defaultTextureType: Accessor<TextureType>;

  /** The palette currently selected for viewing or editing, or null if none. */
  readonly selectedPalette: Accessor<EditablePalette | null>;
  /** Set the palette being viewed/edited. */
  readonly setSelectedPalette: Setter<EditablePalette | null>;

  /** Whether the edit panel is adding a new palette or editing an existing one. */
  readonly editMode: Accessor<EditMode>;
  /** Set the current edit mode ('add' | 'edit'). */
  readonly setEditMode: Setter<EditMode>;
  /** The full list of available palettes sourced from the API service. Empty while loading. */
  readonly availablePalettes: Accessor<readonly EditablePalette[]>;
  /**
   * Whether the Color Palette API feature is enabled. When false, tiles are not
   * recoloured from a per-measurement palette and only the default palette is used.
   */
  readonly colorPaletteEnabled: Accessor<boolean>;

  /** The project id passed to the provider. */
  readonly projectId: Accessor<string>;

  /**
   * The Trimble Connect project TRN, or undefined when no region is configured.
   * Required by the palette write mutations when the Color Palette API is enabled.
   */
  readonly tenantTrn: Accessor<string | undefined>;

  /** Persist changes to the currently selected palette. */
  savePalette(palette: EditablePalette): void;

  /** Select the project default palette (or the first available palette). */
  selectDefaultPalette(): void;

  /** Enter add-palette mode, preserving the current selection for cancel. */
  beginAddPalette(): void;

  /** Restore selection when leaving add mode without saving. */
  cancelPaletteEdit(): void;

  /** Clear add-mode snapshot after a successful save. */
  completePaletteEdit(): void;

  setUseDefaultPalette(useDefault: boolean): void;
}

const VolumeHeatmapContext = createContext<VolumeHeatmapContextValue>();

interface VolumeHeatmapProviderProps {
  /** Connect ProjectId */
  projectId: string;
  /** Feature flag to determine if all palettes from the API are available or just the default. */
  enableVolumeHeatmapColorPalette: boolean;
  /**
   * Trimble Connect region for the project. Combined with `projectId` to build
   * the tenant TRN used when reading palettes from the ColorPalettesAPI.
   */
  tcRegion?: string;
}

export const VolumeHeatmapProvider: ParentComponent<
  VolumeHeatmapProviderProps
> = (props): JSXElement => {
  const [selectedPalette, setSelectedPalette] =
    createSignal<EditablePalette | null>(null);

  const [useDefaultPalette, setUseDefaultPalette] = createSignal(false);

  const projectId = (): string => props.projectId;

  const queryClient = useQueryClient();
  const heatmapPaletteApiService = new HeatmapPaletteApiService(queryClient);

  const tenantTrn = (): string | undefined =>
    props.tcRegion
      ? buildProjectTenantTrn(props.projectId, props.tcRegion)
      : undefined;

  const allPalettesQuery = heatmapPaletteApiService.useAllPalettesQuery(
    tenantTrn,
    () => props.enableVolumeHeatmapColorPalette
  );

  const updateMutation = heatmapPaletteApiService.updatePaletteMutation();
  const addMutation = heatmapPaletteApiService.addPaletteMutation();

  const availablePalettes = createMemo(() => {
    if (props.enableVolumeHeatmapColorPalette) {
      return allPalettesQuery.data ?? [];
    }

    // With the flag off there is no palette catalogue to edit: the heatmap uses
    // the single built-in default palette.
    return [BACKEND_DEFAULT_PALETTE];
  });

  createEffect(() => {
    const palettes = availablePalettes();
    const shouldApplyDefault = useDefaultPalette();
    if (
      shouldApplyDefault &&
      palettes.length > 0 &&
      untrack(selectedPalette) === null
    ) {
      setSelectedPalette(
        palettes.find((p) => p.default) ?? palettes[0] ?? null
      );
    }
  });

  const defaultPalette = createMemo((): EditablePalette | null => {
    const palettes = availablePalettes();
    if (palettes.length === 0) return null;
    return palettes.find((p) => p.default) ?? palettes[0];
  });

  const defaultStops = createMemo(() => defaultPalette()?.stops ?? []);

  const defaultTextureType = createMemo<TextureType>(
    () => defaultPalette()?.textureType ?? 'gradient'
  );

  const [editMode, setEditMode] = createSignal<EditMode>('edit');

  const [paletteSnapshotBeforeAdd, setPaletteSnapshotBeforeAdd] =
    createSignal<EditablePalette | null>(null);

  function savePalette(palette: EditablePalette): void {
    const exists = availablePalettes().some((p) => p.id === palette.id);
    const vars: PaletteWriteVars = {
      palette,
      tenantTrn: tenantTrn(),
    };
    const mutation = exists ? updateMutation : addMutation;
    // Reconcile to the persisted palette on success: for a new palette this
    // swaps in the backend-assigned id so subsequent selection/edits resolve.
    // Guard against clobbering a newer choice: the write resolves after
    // handleSave navigates back, so only reconcile when the palette we saved is
    // still the active selection.
    mutation.mutate(vars, {
      onSuccess: (saved) => {
        if (untrack(selectedPalette)?.id === palette.id) {
          setSelectedPalette(saved);
        }
      },
    });
    // Optimistically reflect the edit while the write and refetch are in flight.
    setSelectedPalette(palette);
  }

  function selectDefaultPalette(): void {
    const palettes = availablePalettes();
    if (palettes.length === 0) {
      setSelectedPalette(null);
      return;
    }
    setSelectedPalette(palettes.find((p) => p.default) ?? palettes[0] ?? null);
  }

  function beginAddPalette(): void {
    setPaletteSnapshotBeforeAdd(untrack(selectedPalette));
    setSelectedPalette(null);
    setEditMode('add');
  }

  function cancelPaletteEdit(): void {
    if (editMode() !== 'add') {
      return;
    }
    const snapshot = paletteSnapshotBeforeAdd();
    if (snapshot) {
      setSelectedPalette(snapshot);
    } else {
      selectDefaultPalette();
    }
    setPaletteSnapshotBeforeAdd(null);
    setEditMode('edit');
  }

  function completePaletteEdit(): void {
    setPaletteSnapshotBeforeAdd(null);
    setEditMode('edit');
  }

  return (
    <VolumeHeatmapContext.Provider
      value={{
        projectId,
        tenantTrn,
        defaultPalette,
        defaultStops,
        defaultTextureType,
        selectedPalette,
        setSelectedPalette,
        editMode,
        setEditMode,
        availablePalettes,
        colorPaletteEnabled: () => props.enableVolumeHeatmapColorPalette,
        savePalette,
        selectDefaultPalette,
        beginAddPalette,
        cancelPaletteEdit,
        completePaletteEdit,
        setUseDefaultPalette,
      }}
    >
      {props.children}
    </VolumeHeatmapContext.Provider>
  );
};

export function useVolumeHeatmap(): VolumeHeatmapContextValue {
  const context = useContext(VolumeHeatmapContext);
  if (!context)
    throw new Error(
      'useVolumeHeatmap must be used within a VolumeHeatmapProvider'
    );
  return context;
}
